/* eslint-disable complexity */
/**
 * MultiSelect Component
 *
 * A custom multi-select component that keeps the dropdown open after each selection.
 *
 * **Why this component exists:**
 * Material-UI's Select component closes automatically after each selection in multi-select mode.
 * This component was created to keep the dropdown open, allowing users to make multiple selections
 * without having to reopen the dropdown each time.
 *
 * **The remounting problem:**
 * The parent component (GenericAddPatientModalContent) was remounting the FormSymptomsSelect
 * component on every form value change. When field.onChange() was called, it triggered a re-render
 * of the parent, which caused the MultiSelect component to unmount and remount, losing its internal
 * `open` state (which defaults to `false`).
 *
 * **The solution:**
 * We use module-level Maps (openStateMap and scrollPositionMap) to persist state across component
 * remounts. These Maps store the open state and scroll position keyed by the field name, so even
 * if the component remounts, it can restore its previous state. We also use a `shouldStayOpenRef` to
 * prevent the dropdown from closing immediately after a selection is made, giving the component time
 * to restore its open state from the module-level map.
 *
 * **Key techniques:**
 * - Module-level state (Maps) that survive component remounts
 * - useRef to track "should stay open" flag across renders
 * - Immediate setOpen(true) in handleOptionToggle to prevent closing
 * - Manual click-outside detection instead of ClickAwayListener
 * - Scroll position preservation across remounts
 */
import React, { useState, useRef, useEffect } from "react";
import { Control, Controller } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Popper,
  Paper,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
} from "@mui/material";
import { Chip } from "@/components/ui/chip";
import { Icon } from "@/components/Icon";

const autocompleteOffSlotProps = {
  htmlInput: {
    autoComplete: "off",
  },
};

// Module-level state to survive component remounts
const openStateMap = new Map<string, boolean>();
const scrollPositionMap = new Map<string, number>();

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  name: string;
  control: Control<any>;
  label?: string;
  options: MultiSelectOption[];
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  sx?: any;
}

const MultiSelectContent: React.FC<{
  field: any;
  fieldState: any;
  options: MultiSelectOption[];
  label?: string;
  placeholder: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  sx?: any;
}> = ({
  field,
  fieldState,
  options,
  label,
  placeholder,
  required,
  error,
  helperText,
  disabled,
  sx,
}) => {
  // Use field name as key to persist state across remounts
  const stateKey = `multiselect-${field.name}`;
  const [open, setOpen] = useState(() => openStateMap.get(stateKey) || false);
  const anchorRef = useRef<HTMLFieldSetElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const previousValueRef = useRef<any>(field.value);
  const shouldStayOpenRef = useRef(false);

  const selectedValues = field.value || [];
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  // Persist open state to module-level map
  useEffect(() => {
    openStateMap.set(stateKey, open);
  }, [open, stateKey]);



  // Detect when field.value changes (selection made) and keep open
  const fieldValueChanged = previousValueRef.current !== field.value;
  if (fieldValueChanged && open) {
    shouldStayOpenRef.current = true;
    previousValueRef.current = field.value;
  } else if (fieldValueChanged) {
    previousValueRef.current = field.value;
  }


  // Restore open state from module-level map if we just remounted
  useEffect(() => {
    const persistedOpen = openStateMap.get(stateKey);
    if (persistedOpen && !open && shouldStayOpenRef.current) {
      setOpen(true);
    }
  }, [stateKey, open]);

  // Restore scroll position when Paper is mounted and open
  useEffect(() => {
    if (open && paperRef.current) {
      const savedScrollPosition = scrollPositionMap.get(stateKey) || 0;
      paperRef.current.scrollTop = savedScrollPosition;
    }
  }, [open, stateKey]);

  // Force open if we should stay open but aren't - use useEffect to avoid setState during render
  useEffect(() => {
    if (shouldStayOpenRef.current && !open) {
      setOpen(true);
    }
  }, [open]);

  const handleOpen = () => {
    if (!disabled) {
      shouldStayOpenRef.current = false; // Reset when manually opening
      setOpen(true);
      openStateMap.set(stateKey, true);
    }
  };


  const handleOptionToggle = (optionValue: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark that we should stay open BEFORE changing the value
    shouldStayOpenRef.current = true;
    openStateMap.set(stateKey, true); // Persist to module-level map

    const currentValues = field.value || [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v: string) => v !== optionValue)
      : [...currentValues, optionValue];

    field.onChange(newValues);

    // Immediately set open to true to prevent closing
    setOpen(true);
    openStateMap.set(stateKey, true);

    // Reset shouldStayOpen after a delay to allow closing again
    setTimeout(() => {
      shouldStayOpenRef.current = false;
    }, 300);
  };

  // Preserve open state after form updates
  useEffect(() => {
    if (shouldStayOpenRef.current && !open) {
      setOpen(true);
    }
  }, [open, field.value]);

  // Reset shouldStayOpen when manually closing
  useEffect(() => {
    if (!open && shouldStayOpenRef.current) {
      // Only reset if we're not in the middle of a selection
      // Check if value changed recently
      const valueJustChanged = previousValueRef.current !== field.value;
      if (!valueJustChanged) {
        shouldStayOpenRef.current = false;
      }
    }
  }, [open, field.value]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInAnchor = anchorRef.current?.contains(target);
      const isInPopper = popperRef.current?.contains(target);

      // Don't close if clicking inside anchor or popper
      if (isInAnchor || isInPopper) {
        return;
      }

      // Allow closing on outside click (even if shouldStayOpen is true, user explicitly clicked outside)
      shouldStayOpenRef.current = false;
      openStateMap.set(stateKey, false);
      setOpen(false);
    };

    if (open) {
      // Use a longer delay to let form re-renders complete
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside, true);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside, true);
      };
    }
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        shouldStayOpenRef.current = false;
        openStateMap.set(stateKey, false);
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, stateKey]);

  return (
    <Box sx={{ width: "100%", position: "relative", ...sx }}>
      <FormControl
        fullWidth
        error={!!fieldState.error || error}
        disabled={disabled}
      >
        {label && (
          <InputLabel
            shrink={true}
            sx={{
              backgroundColor: "#fff",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            {label}
            {required && <span style={{ color: "#d32f2f" }}> *</span>}
          </InputLabel>
        )}
        <fieldset
          ref={anchorRef}
          tabIndex={disabled ? -1 : 0}
          style={{
            position: "relative",
            border: `1px solid ${error || fieldState.error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)"}`,
            borderRadius: "4px",
            padding: "8px 16px",
            margin: 0,
            minHeight: "39px",
            height: "auto",
            cursor: disabled ? "default" : "pointer",
            backgroundColor: disabled ? "rgba(0, 0, 0, 0.12)" : "#fff",
            transition: "border-color 0.2s",
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "stretch",
            outline: "none",
          }}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handleOpen();
            }
          }}
          onMouseEnter={(e) => {
            if (!disabled && !error && !fieldState.error) {
              e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.87)";
            }
          }}
          onMouseLeave={(e) => {
            if (!error && !fieldState.error) {
              e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.23)";
            }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: "100%", minHeight: "23px" }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                flex: 1,
                alignItems: "center",
                minHeight: "23px",
              }}
            >
              {selectedOptions.length > 0 ? (
                selectedOptions.map((option) => (
                  <Chip
                    key={option.value}
                    size="small"
                    label={option.label}
                    onDelete={() => {
                      const currentValues = field.value || [];
                      const newValues = currentValues.filter((v: string) => v !== option.value);
                      field.onChange(newValues);
                    }}
                  />
                ))
              ) : (
                <span style={{ color: "#9e9e9e" }}>{placeholder}</span>
              )}
            </Box>
            <Box
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                icon="chevrondown"
                style={{ color: "rgba(0, 0, 0, 0.54)" }}
              />
            </Box>
          </Stack>
        </fieldset>
        {(helperText || fieldState.error) && (
          <FormHelperText>
            {fieldState.error?.message || helperText || " "}
          </FormHelperText>
        )}
      </FormControl>

      <Popper
        open={open && !!anchorRef.current}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <Paper
          ref={(node) => {
            popperRef.current = node;
            paperRef.current = node;
            // Restore scroll position when Paper mounts
            if (node) {
              const savedScrollPosition = scrollPositionMap.get(stateKey) || 0;
              node.scrollTop = savedScrollPosition;
            }
          }}
          sx={{
            minWidth: anchorRef.current?.offsetWidth || 200,
            maxHeight: 300,
            overflow: "auto",
            boxShadow: 3,
          }}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss
            e.stopPropagation(); // Stop event from reaching document
          }}
          onClick={(e) => {
            e.stopPropagation(); // Stop event from reaching document
          }}
          onScroll={(e) => {
            // Save scroll position
            const scrollTop = e.currentTarget.scrollTop;
            scrollPositionMap.set(stateKey, scrollTop);
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOptionToggle(option.value, e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionToggle(option.value, e);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Paper>
      </Popper>
    </Box>
  );
};

export const MultiSelect: React.FC<MultiSelectProps> = (props) => {
  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field, fieldState }) => (
        <MultiSelectContent
          field={field}
          fieldState={fieldState}
          options={props.options}
          label={props.label}
          placeholder={props.placeholder || "Select options..."}
          required={props.required}
          error={props.error}
          helperText={props.helperText}
          disabled={props.disabled}
          sx={props.sx}
        />
      )}
    />
  );
};

// Module-level state for MultiSelectAutocomplete to persist open state across remounts
const autocompleteOpenStateMap = new Map<string, boolean>();
const autocompleteJustSelectedMap = new Map<string, boolean>();

// MultiSelectAutocomplete - MUI Autocomplete-based multi-select with type-ahead support
export const MultiSelectAutocomplete: React.FC<MultiSelectProps> = (props) => {
  const stateKey = `autocomplete-${props.name}`;
  const [open, setOpen] = React.useState(() => autocompleteOpenStateMap.get(stateKey) || false);
  const justSelectedRef = React.useRef(false);

  // Persist open state to module-level map
  React.useEffect(() => {
    autocompleteOpenStateMap.set(stateKey, open);
  }, [open, stateKey]);

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field, fieldState }) => {
        const hasError = props.error !== undefined ? props.error : !!fieldState.error;
        const helperText = props.helperText !== undefined
          ? props.helperText
          : (fieldState.error ? fieldState.error.message : " ");

        // Convert selected values to option objects
        const selectedOptions = props.options.filter(opt =>
          (field.value || []).includes(opt.value)
        );

        return (
          <Autocomplete
            multiple
            value={selectedOptions}
            onChange={(_, newValue) => {
              field.onChange(newValue.map(opt => opt.value));
              // Mark that we just made a selection - keep dropdown open
              justSelectedRef.current = true;
              autocompleteJustSelectedMap.set(stateKey, true);
              setOpen(true);
              autocompleteOpenStateMap.set(stateKey, true);
              // Reset the flag after a short delay (50ms should be enough to catch immediate close attempts)
              setTimeout(() => {
                justSelectedRef.current = false;
                autocompleteJustSelectedMap.set(stateKey, false);
              }, 50);
            }}
            onOpen={() => {
              justSelectedRef.current = false;
              autocompleteJustSelectedMap.set(stateKey, false);
              setOpen(true);
              autocompleteOpenStateMap.set(stateKey, true);
            }}
            onClose={(_, reason) => {
              // If we just made a selection (within last 50ms), prevent closing UNLESS user explicitly wants to close
              // Reasons that indicate user intent to close: "escape", "toggleInput", "blur" (click outside)
              const justSelected = justSelectedRef.current || autocompleteJustSelectedMap.get(stateKey);
              const userWantsToClose = reason === "escape" || reason === "toggleInput" || reason === "blur";

              if (justSelected && !userWantsToClose) {
                // Restore open state after MUI's close handler completes
                setTimeout(() => {
                  setOpen(true);
                  autocompleteOpenStateMap.set(stateKey, true);
                }, 0);
                return; // Don't close
              }
              // User explicitly closed (escape, click outside, toggle, or enough time passed since selection)
              setOpen(false);
              autocompleteOpenStateMap.set(stateKey, false);
              justSelectedRef.current = false;
              autocompleteJustSelectedMap.set(stateKey, false);
            }}
            open={open}
            options={props.options}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={props.disabled}
            disableCloseOnSelect
            fullWidth
            autoHighlight
            openOnFocus
            sx={{
              width: "100%",
              maxWidth: "100%",
              "& .MuiAutocomplete-inputRoot": {
                minHeight: "38.99px",
                padding: "4.5px 9px !important",
                flexWrap: "wrap",
                boxSizing: "border-box",
              },
              "& .MuiAutocomplete-input": {
                minWidth: "30px !important",
                padding: "4px 0 !important",
              },
              "& .MuiAutocomplete-tag": {
                height: "20px",
                fontSize: "0.75rem",
                margin: "2px",
                maxWidth: "calc(100% - 60px)",
              },
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingLeft: "8px",
                paddingRight: "8px",
              },
              "& .MuiChip-deleteIcon": {
                fontSize: "14px",
                marginRight: "4px",
              },
              ...props.sx,
            }}
            renderOption={(optionProps, option, { selected }) => (
              <li {...optionProps} key={option.value}>
                <Checkbox
                  checked={selected}
                  sx={{ mr: 1 }}
                />
                <ListItemText primary={option.label} />
              </li>
            )}
            renderInput={({ inputProps, ...params }) => (
              <TextField
                {...params}
                label={props.label}
                placeholder={selectedOptions.length === 0 ? (props.placeholder || "Select options...") : ""}
                error={hasError}
                helperText={helperText}
                variant="outlined"
                required={props.required}
                autoComplete="off"
                slotProps={{
                  htmlInput: {
                    ...inputProps,
                    ...autocompleteOffSlotProps.htmlInput,
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
};
